import React from "react";
import {
  SkeletonBase,
  SkeletonBlock,
  SkeletonLine,
} from "@ui-components/Skeleton/GenericSkeleton";

type PageVariant = "default" | "port";

const CARD_SHELL =
  "rounded-lg border border-semantic-border-primary bg-semantic-bg-primary";

/** One detail card: title strip + 4 label/value-style rows */
export const DetailCardSkeleton: React.FC<{ className?: string }> = ({
  className = "",
}) => (
  <div className={`${CARD_SHELL} p-4 ${className}`.trim()}>
    <SkeletonLine w="42%" h={14} className="mb-4" />
    <div className="space-y-3">
      {[
        "88%",
        "72%",
        "90%",
        "56%",
      ].map((w, i) => (
        <SkeletonLine key={i} w={w} h={12} />
      ))}
    </div>
  </div>
);

export type ServiceDetailsTableSkeletonProps = {
  /**
   * `activity` — rows like StatusTable (pill + date column).
   * `data` — column headers + grid rows (e.g. port connections table).
   */
  variant?: "activity" | "data";
  /** Body rows under the header */
  rows?: number;
  /** `data` variant: columns per row */
  columns?: number;
  className?: string;
};

/**
 * Table-shaped skeleton for detail pages (activity/history lists or data tables).
 */
export const ServiceDetailsTableSkeleton: React.FC<
  ServiceDetailsTableSkeletonProps
> = ({
  variant = "activity",
  rows = 4,
  columns = 5,
  className = "",
}) => {
  if (variant === "data") {
    const dataRows = Math.max(1, rows);
    const cols = Math.max(2, columns);
    return (
      <div className={`flex justify-start py-4 ${className}`.trim()}>
        <div className={`w-full overflow-hidden ${CARD_SHELL}`}>
          <div
            className="grid gap-3 px-4 py-3 border-b border-semantic-border-primary"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: cols }).map((_, i) => (
              <SkeletonLine key={`h-${i}`} w="70%" h={12} />
            ))}
          </div>
          {Array.from({ length: dataRows }).map((_, ri) => (
            <div
              key={ri}
              className="grid gap-3 px-4 py-3 border-b border-semantic-border-primary last:border-0"
              style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
            >
              {Array.from({ length: cols }).map((_, ci) => (
                <SkeletonLine
                  key={ci}
                  w={ci === 0 ? "85%" : "92%"}
                  h={14}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activityRows = Math.max(1, rows);
  return (
    <div className={`flex justify-start py-4 ${className}`.trim()}>
      <div className="w-full md:w-3/4">
        <div className={`overflow-hidden ${CARD_SHELL}`}>
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-semantic-border-primary">
            <SkeletonLine w={140} h={16} />
            <SkeletonLine w={112} h={14} />
          </div>
          <div className="px-4 py-1 overflow-x-auto">
            {Array.from({ length: activityRows }).map((_, i) => (
              <div
                key={i}
                className="flex flex-nowrap items-center gap-4 py-3 border-b border-semantic-border-primary last:border-0 w-full min-w-[500px]"
              >
                <SkeletonBase
                  h={28}
                  w={90}
                  roundedClass="rounded-full"
                  className="flex-shrink-0"
                  aria-hidden
                />
                <div className="flex-1 flex justify-end min-w-0">
                  <SkeletonLine w="min(200px, 55%)" h={14} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CHART_SHELL = `${CARD_SHELL} p-4`;

const ChartAreaSkeleton: React.FC = () => (
  <div className={CHART_SHELL}>
    <SkeletonLine w="38%" h={14} className="mb-4" />
    <SkeletonBlock h={220} className="rounded-md w-full" />
  </div>
);

const CARD_COUNT = 6;

/**
 * Inline loading layout for Kontrol service detail pages (cards + chart + tables).
 * Aligns with Backbone-style skeletons: structured rows instead of single huge blocks.
 */
export const ServiceDetailsPageSkeleton: React.FC<{
  variant?: PageVariant;
}> = ({ variant = "default" }) => {
  return (
    <div
      className="flex flex-col gap-4 py-4"
      role="status"
      aria-busy
      aria-label="Loading details"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-auto">
        {Array.from({ length: CARD_COUNT }).map((_, i) => (
          <DetailCardSkeleton key={i} />
        ))}
      </div>

      <ChartAreaSkeleton />

      {variant === "port" ? (
        <>
          <ServiceDetailsTableSkeleton variant="data" rows={3} columns={5} />
          <ServiceDetailsTableSkeleton variant="activity" rows={4} />
        </>
      ) : (
        <ServiceDetailsTableSkeleton variant="activity" rows={4} />
      )}
    </div>
  );
};
